import { c as _c } from "react/compiler-runtime";
/**
 * FormField — Main dispatcher component for dynamic form fields.
 *
 * Reads the field schema (property) and delegates rendering to one of:
 * - FormFieldConnector  (Polymarket, Slack, Telegram, email, wallet, etc.)
 * - FormFieldBlockchain  (chain, token, address, contract, ABI, etc.)
 * - FormFieldSpecial     (JSON, code, array, delay, filter, etc.)
 * - Inline basic fields  (boolean, number, date, enum, default string)
 *
 * Re-exports the `FormFieldInternalProps` type so sub-files can import it.
 */
import { formatLabel } from "@/utils/string";
import type { Edge, Node } from "@xyflow/react";
import * as React from "react";
import { memo, useState } from "react";
import { FieldDescription } from "./FieldDescription";
import BooleanInput from "./BooleanInput";
import DateTimeInput from "./DateTimeInput";
import NumberInput from "./NumberInput";
import SelectInput from "./SelectInput";
import VariableInput from "./VariableInput";
import { TickerSelect } from "@/components/ui/ticker-select";
import { renderBlockchainField } from "./FormFieldBlockchain";
import { renderConnectorField } from "./FormFieldConnector";
import { renderSpecialField } from "./FormFieldSpecial";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

interface NodeDefinition {
  type: string;
  resultSchema?: {
    properties?: Record<string, {
      description?: string;
      type?: string;
    }>;
  };
}
const SIDEBAR_DESCRIPTION_HIDE_RULES = [{
  fieldName: "chainId",
  description: "Chain ID (1=Ethereum, 137=Polygon, 56=BSC, 8453=Base, etc.)"
}, {
  fieldName: "tokenAddress",
  description: "The ERC20 token contract address"
}] as const;
function shouldHideSidebarDescription(fieldName: string, description: unknown): boolean {
  if (typeof description !== "string") return false;
  return SIDEBAR_DESCRIPTION_HIDE_RULES.some(rule => rule.fieldName === fieldName && rule.description === description.trim());
}
function renderExchangeOption(option: string, label: string) {
  return <span className="flex items-center gap-2">
      <img src={`https://ai-icon.b3.fun/logo/${option}`} alt="" className="border-ds-border-default h-5 w-5 rounded-full border bg-white object-cover" onError={e => {
      (e.target as HTMLImageElement).style.display = "none";
    }} />
      {label}
    </span>;
}
interface FormFieldProps {
  name: string;
  property: any;
  value: any;
  onChange: (name: string, value: any) => void;
  allValues?: Record<string, any>;
  currentNodeId?: string;
  currentNodeType?: string; // Node type for conditional rendering
  allNodes?: Node[];
  allEdges?: Edge[];
  currentNodePayload?: Record<string, any>; // Direct access to current node's payload (no debouncing)
  hideLabel?: boolean; // Hide the label and description (useful for preview contexts)
  validationError?: string; // Validation error message to highlight this field
  nodeDefinitions?: NodeDefinition[];
  schemaProperties?: Record<string, any>; // All properties in the schema for checking sibling fields
  workflowWalletAddress?: string; // Workflow wallet address for token transfer/deposit prompts
  showAnySpendFundingCta?: boolean; // Show AnySpend funding shortcut for token fields
  connectorId?: string; // Connector ID for connector-dependent fields (e.g., Slack channel selector)
  connectorType?: string; // Connector type (e.g., "pipedream:gmail")
}

/**
 * Internal props passed to sub-file render functions.
 * Exported so FormFieldBlockchain / FormFieldConnector / FormFieldSpecial
 * can import the type without circular dependency on the component itself.
 */
export interface FormFieldInternalProps {
  name: string;
  property: any;
  value: any;
  onChange: (name: string, value: any) => void;
  allValues: Record<string, any>;
  currentNodeId: string;
  currentNodeType: string;
  allNodes: Node[];
  allEdges: Edge[];
  currentNodePayload: Record<string, any>;
  hideLabel: boolean;
  nodeDefinitions: NodeDefinition[];
  schemaProperties: Record<string, any>;
  workflowWalletAddress?: string;
  showAnySpendFundingCta?: boolean;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function FormField(t0) {
  const $ = _c(133);
  const {
    name,
    property: rawProperty,
    value,
    onChange,
    allValues: t1,
    currentNodeId: t2,
    currentNodeType: t3,
    allNodes: t4,
    allEdges: t5,
    currentNodePayload: t6,
    hideLabel: t7,
    validationError,
    nodeDefinitions: t8,
    schemaProperties: t9,
    workflowWalletAddress,
    showAnySpendFundingCta: t10,
    connectorId: connectorIdProp,
    connectorType: connectorTypeProp
  } = t0;
  let t11;
  if ($[0] !== t1) {
    t11 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t11;
  } else {
    t11 = $[1];
  }
  const allValues = t11;
  const currentNodeId = t2 === undefined ? "" : t2;
  const currentNodeType = t3 === undefined ? "" : t3;
  let t12;
  if ($[2] !== t4) {
    t12 = t4 === undefined ? [] : t4;
    $[2] = t4;
    $[3] = t12;
  } else {
    t12 = $[3];
  }
  const allNodes = t12;
  let t13;
  if ($[4] !== t5) {
    t13 = t5 === undefined ? [] : t5;
    $[4] = t5;
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  const allEdges = t13;
  let t14;
  if ($[6] !== t6) {
    t14 = t6 === undefined ? {} : t6;
    $[6] = t6;
    $[7] = t14;
  } else {
    t14 = $[7];
  }
  const currentNodePayload = t14;
  const hideLabel = t7 === undefined ? false : t7;
  let t15;
  if ($[8] !== t8) {
    t15 = t8 === undefined ? [] : t8;
    $[8] = t8;
    $[9] = t15;
  } else {
    t15 = $[9];
  }
  const nodeDefinitions = t15;
  let t16;
  if ($[10] !== t9) {
    t16 = t9 === undefined ? {} : t9;
    $[10] = t9;
    $[11] = t16;
  } else {
    t16 = $[11];
  }
  const schemaProperties = t16;
  const showAnySpendFundingCta = t10 === undefined ? false : t10;
  let t17;
  bb0: {
    if (!rawProperty || !shouldHideSidebarDescription(name, rawProperty.description)) {
      t17 = rawProperty;
      break bb0;
    }
    let t18;
    if ($[12] !== rawProperty) {
      t18 = {
        ...rawProperty,
        description: undefined
      };
      $[12] = rawProperty;
      $[13] = t18;
    } else {
      t18 = $[13];
    }
    t17 = t18;
  }
  const property = t17;
  let t18;
  if ($[14] !== allNodes || $[15] !== currentNodeId) {
    let t19;
    if ($[17] !== currentNodeId) {
      t19 = n => n.id === currentNodeId;
      $[17] = currentNodeId;
      $[18] = t19;
    } else {
      t19 = $[18];
    }
    t18 = allNodes.find(t19);
    $[14] = allNodes;
    $[15] = currentNodeId;
    $[16] = t18;
  } else {
    t18 = $[16];
  }
  const currentNode = t18;
  const connectorId = connectorIdProp ?? (currentNode?.data?.connector as Record<string, unknown>)?.id as string | undefined;
  let t19;
  if ($[19] !== connectorTypeProp || $[20] !== currentNodeType || $[21] !== nodeDefinitions) {
    t19 = connectorTypeProp ?? (nodeDefinitions.find(d => d.type === currentNodeType) as any)?.connector?.type as string | undefined;
    $[19] = connectorTypeProp;
    $[20] = currentNodeType;
    $[21] = nodeDefinitions;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  const connectorType = t19;
  const [abiSelectorMode, setAbiSelectorMode] = React.useState("auto");
  let t20;
  if ($[23] !== name || $[24] !== onChange) {
    t20 = newValue => {
      onChange(name, newValue);
    };
    $[23] = name;
    $[24] = onChange;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  const handleChange = t20;
  const validationData = {
    ...currentNodePayload,
    ...allValues
  };
  const [forceVariable, setForceVariable] = useState(false);
  let t21;
  if ($[26] !== value) {
    t21 = typeof value === "string" && value.includes("{{");
    $[26] = value;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  const isVariableValue = t21;
  const isEvmReadWrite = currentNodeType === "evm-read" || currentNodeType === "evm-write";
  const isProjectMode = allValues.contractSource === "project";
  const hasWorkflowContext = !!currentNodeId && allNodes.length > 0;
  let t22;
  if ($[28] !== allEdges || $[29] !== allNodes || $[30] !== currentNodeId || $[31] !== forceVariable || $[32] !== handleChange || $[33] !== hasWorkflowContext || $[34] !== hideLabel || $[35] !== isVariableValue || $[36] !== name || $[37] !== nodeDefinitions || $[38] !== property || $[39] !== value) {
    t22 = element => {
      if (!hasWorkflowContext) {
        return element;
      }
      const label = hideLabel ? "" : property.title || formatLabel(name);
      const isVarMode = forceVariable || isVariableValue;
      const toggleButton = <button type="button" onClick={() => {
        if (isVarMode) {
          setForceVariable(false);
          if (isVariableValue) {
            handleChange(undefined);
          }
        } else {
          setForceVariable(true);
        }
      }} className={`text-3xs font-mono leading-none transition-colors ${isVarMode ? "text-blue-600 dark:text-blue-400" : "text-ds-text-disabled hover:text-ds-text-subtle dark:text-ds-text-muted dark:hover:text-zinc-300"}`} title={isVarMode ? "Switch to selector" : "Use variable expression"}>{"{{ }}"}</button>;
      if (isVarMode) {
        const stringValue = value !== undefined && value !== null ? String(value) : "";
        return <div className="space-y-2"><div className="flex items-center justify-between">{label ? <label className="field-label">{label}{property.required && <span className="ml-1 text-red-500">*</span>}</label> : <span />}{toggleButton}</div>{!hideLabel && property.description && <FieldDescription text={property.description} />}<VariableInput label="" description="" value={stringValue} onChange={handleChange} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} placeholder={`Type {{ for variables`} autoShowDropdown={forceVariable} /></div>;
      }
      return React.cloneElement(element, {
        headerRight: toggleButton
      } as Partial<typeof element.props>);
    };
    $[28] = allEdges;
    $[29] = allNodes;
    $[30] = currentNodeId;
    $[31] = forceVariable;
    $[32] = handleChange;
    $[33] = hasWorkflowContext;
    $[34] = hideLabel;
    $[35] = isVariableValue;
    $[36] = name;
    $[37] = nodeDefinitions;
    $[38] = property;
    $[39] = value;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const withVariableToggle = t22;
  let t23;
  if ($[41] !== validationError) {
    t23 = element_0 => {
      if (!validationError) {
        return element_0;
      }
      return <div className="rounded-lg ring-2 ring-red-400/60 ring-offset-1 ring-offset-transparent transition-all dark:ring-red-500/50">{element_0}<p className="text-2xs mt-1 px-1 text-red-500 dark:text-red-400">{validationError}</p></div>;
    };
    $[41] = validationError;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  const wrapWithError = t23;
  const internalProps = {
    name,
    property,
    value,
    onChange,
    allValues,
    currentNodeId,
    currentNodeType,
    allNodes,
    allEdges,
    currentNodePayload,
    hideLabel,
    nodeDefinitions,
    schemaProperties,
    workflowWalletAddress,
    showAnySpendFundingCta
  };
  const connectorResult = renderConnectorField({
    ...internalProps,
    handleChange,
    connectorId,
    connectorType,
    withVariableToggle
  });
  if (connectorResult !== null) {
    return wrapWithError(connectorResult);
  }
  const blockchainResult = renderBlockchainField({
    ...internalProps,
    handleChange,
    validationData,
    isEvmReadWrite,
    isProjectMode,
    abiSelectorMode,
    setAbiSelectorMode,
    withVariableToggle
  });
  if (blockchainResult !== null) {
    return wrapWithError(blockchainResult);
  }
  const specialResult = renderSpecialField({
    ...internalProps,
    handleChange,
    validationData,
    withVariableToggle
  });
  if (specialResult !== null) {
    return wrapWithError(specialResult);
  }
  if (property.type === "boolean") {
    let t24;
    if ($[43] !== hideLabel || $[44] !== name || $[45] !== property.title) {
      t24 = hideLabel ? "" : property.title || formatLabel(name);
      $[43] = hideLabel;
      $[44] = name;
      $[45] = property.title;
      $[46] = t24;
    } else {
      t24 = $[46];
    }
    const t25 = hideLabel ? "" : property.description;
    const t26 = value ?? property.default ?? false;
    let t27;
    if ($[47] !== handleChange || $[48] !== t24 || $[49] !== t25 || $[50] !== t26) {
      t27 = <BooleanInput label={t24} description={t25} value={t26} onChange={handleChange} />;
      $[47] = handleChange;
      $[48] = t24;
      $[49] = t25;
      $[50] = t26;
      $[51] = t27;
    } else {
      t27 = $[51];
    }
    let t28;
    if ($[52] !== t27 || $[53] !== withVariableToggle || $[54] !== wrapWithError) {
      t28 = wrapWithError(withVariableToggle(t27));
      $[52] = t27;
      $[53] = withVariableToggle;
      $[54] = wrapWithError;
      $[55] = t28;
    } else {
      t28 = $[55];
    }
    return t28;
  }
  if (property.type === "number" || property.type === "integer") {
    let t24;
    if ($[56] !== hideLabel || $[57] !== name || $[58] !== property.title) {
      t24 = hideLabel ? "" : property.title || formatLabel(name);
      $[56] = hideLabel;
      $[57] = name;
      $[58] = property.title;
      $[59] = t24;
    } else {
      t24 = $[59];
    }
    const t25 = hideLabel ? "" : property.description;
    const t26 = value ?? property.default;
    const t27 = property.multipleOf ?? (property.type === "integer" ? 1 : undefined);
    let t28;
    if ($[60] !== handleChange || $[61] !== property.maximum || $[62] !== property.minimum || $[63] !== t24 || $[64] !== t25 || $[65] !== t26 || $[66] !== t27) {
      t28 = <NumberInput label={t24} description={t25} value={t26} min={property.minimum} max={property.maximum} step={t27} onChange={handleChange} />;
      $[60] = handleChange;
      $[61] = property.maximum;
      $[62] = property.minimum;
      $[63] = t24;
      $[64] = t25;
      $[65] = t26;
      $[66] = t27;
      $[67] = t28;
    } else {
      t28 = $[67];
    }
    let t29;
    if ($[68] !== t28 || $[69] !== withVariableToggle || $[70] !== wrapWithError) {
      t29 = wrapWithError(withVariableToggle(t28));
      $[68] = t28;
      $[69] = withVariableToggle;
      $[70] = wrapWithError;
      $[71] = t29;
    } else {
      t29 = $[71];
    }
    return t29;
  }
  if (property.type === "string" && property.format === "ticker") {
    let t24;
    if ($[72] !== hideLabel || $[73] !== name || $[74] !== property.title) {
      t24 = hideLabel ? "" : property.title || formatLabel(name);
      $[72] = hideLabel;
      $[73] = name;
      $[74] = property.title;
      $[75] = t24;
    } else {
      t24 = $[75];
    }
    const t25 = hideLabel ? "" : property.description;
    const t26 = value as string ?? "";
    let t27;
    if ($[76] !== handleChange || $[77] !== t24 || $[78] !== t25 || $[79] !== t26) {
      t27 = <TickerSelect multiple={false} label={t24} description={t25} value={t26} onChange={handleChange} />;
      $[76] = handleChange;
      $[77] = t24;
      $[78] = t25;
      $[79] = t26;
      $[80] = t27;
    } else {
      t27 = $[80];
    }
    let t28;
    if ($[81] !== t27 || $[82] !== withVariableToggle || $[83] !== wrapWithError) {
      t28 = wrapWithError(withVariableToggle(t27));
      $[81] = t27;
      $[82] = withVariableToggle;
      $[83] = wrapWithError;
      $[84] = t28;
    } else {
      t28 = $[84];
    }
    return t28;
  }
  if (property.type === "date-time" || property.type === "date" || property.format === "date-time" || property.format === "date" || name === "nextRunAt") {
    let t24;
    if ($[85] !== hideLabel || $[86] !== name || $[87] !== property.title) {
      t24 = hideLabel ? "" : property.title || formatLabel(name);
      $[85] = hideLabel;
      $[86] = name;
      $[87] = property.title;
      $[88] = t24;
    } else {
      t24 = $[88];
    }
    const t25 = hideLabel ? "" : property.description;
    let t26;
    if ($[89] !== handleChange || $[90] !== t24 || $[91] !== t25 || $[92] !== value) {
      t26 = <DateTimeInput label={t24} description={t25} value={value} onChange={handleChange} />;
      $[89] = handleChange;
      $[90] = t24;
      $[91] = t25;
      $[92] = value;
      $[93] = t26;
    } else {
      t26 = $[93];
    }
    let t27;
    if ($[94] !== t26 || $[95] !== withVariableToggle || $[96] !== wrapWithError) {
      t27 = wrapWithError(withVariableToggle(t26));
      $[94] = t26;
      $[95] = withVariableToggle;
      $[96] = wrapWithError;
      $[97] = t27;
    } else {
      t27 = $[97];
    }
    return t27;
  }
  if (property.enum) {
    let t24;
    if ($[98] !== hideLabel || $[99] !== name || $[100] !== property.title) {
      t24 = hideLabel ? "" : property.title || formatLabel(name);
      $[98] = hideLabel;
      $[99] = name;
      $[100] = property.title;
      $[101] = t24;
    } else {
      t24 = $[101];
    }
    const t25 = hideLabel ? "" : property.description;
    const t26 = value ?? property.default ?? "";
    const t27 = property.format === "exchange" ? renderExchangeOption : undefined;
    const t28 = property.required || false;
    let t29;
    if ($[102] !== handleChange || $[103] !== property.enum || $[104] !== property.enumNames || $[105] !== t24 || $[106] !== t25 || $[107] !== t26 || $[108] !== t27 || $[109] !== t28) {
      t29 = <SelectInput label={t24} description={t25} value={t26} options={property.enum} optionLabels={property.enumNames} renderOption={t27} onChange={handleChange} required={t28} />;
      $[102] = handleChange;
      $[103] = property.enum;
      $[104] = property.enumNames;
      $[105] = t24;
      $[106] = t25;
      $[107] = t26;
      $[108] = t27;
      $[109] = t28;
      $[110] = t29;
    } else {
      t29 = $[110];
    }
    let t30;
    if ($[111] !== t29 || $[112] !== withVariableToggle || $[113] !== wrapWithError) {
      t30 = wrapWithError(withVariableToggle(t29));
      $[111] = t29;
      $[112] = withVariableToggle;
      $[113] = wrapWithError;
      $[114] = t30;
    } else {
      t30 = $[114];
    }
    return t30;
  }
  let t24;
  if ($[115] !== hideLabel || $[116] !== name || $[117] !== property.title) {
    t24 = hideLabel ? "" : property.title || formatLabel(name);
    $[115] = hideLabel;
    $[116] = name;
    $[117] = property.title;
    $[118] = t24;
  } else {
    t24 = $[118];
  }
  const t25 = hideLabel ? "" : property.description;
  const t26 = value ?? property.default ?? "";
  const t27 = property.required || false;
  let t28;
  if ($[119] !== allEdges || $[120] !== allNodes || $[121] !== currentNodeId || $[122] !== handleChange || $[123] !== nodeDefinitions || $[124] !== property.placeholder || $[125] !== t24 || $[126] !== t25 || $[127] !== t26 || $[128] !== t27) {
    t28 = <VariableInput label={t24} description={t25} value={t26} onChange={handleChange} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} placeholder={property.placeholder} required={t27} />;
    $[119] = allEdges;
    $[120] = allNodes;
    $[121] = currentNodeId;
    $[122] = handleChange;
    $[123] = nodeDefinitions;
    $[124] = property.placeholder;
    $[125] = t24;
    $[126] = t25;
    $[127] = t26;
    $[128] = t27;
    $[129] = t28;
  } else {
    t28 = $[129];
  }
  let t29;
  if ($[130] !== t28 || $[131] !== wrapWithError) {
    t29 = wrapWithError(t28);
    $[130] = t28;
    $[131] = wrapWithError;
    $[132] = t29;
  } else {
    t29 = $[132];
  }
  return t29;
}
export default memo(FormField, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders

  // Never memoize abi, eventAbi, filter, args, functionName, and contractProjectId - always re-render for fresh validation
  if (prevProps.name === "abi" || prevProps.name === "eventAbi" || prevProps.name === "filter" || prevProps.name === "args" || prevProps.name === "functionName" || prevProps.name === "contractProjectId") {
    return false; // Always re-render
  }

  // For polymarket marketUrl field, re-render when any polymarket config field changes
  // This is needed because PolymarketMarketInput handles multiple fields together
  if (prevProps.name === "marketUrl" && prevProps.currentNodeType === "polymarket-place-bet") {
    const polymarketFields = ["mode", "marketUrl", "outcome", "groupOutcome", "tokenId", "side", "tokenIdVariable", "sideVariable", "amountMode", "priceMode", "orderType", "fixedAmount", "dynamicAmount", "multiplier", "minAmount", "maxAmount", "limitPrice", "copyPriceVariable"];
    for (const field of polymarketFields) {
      if (prevProps.allValues?.[field] !== nextProps.allValues?.[field]) {
        return false; // Re-render
      }
    }
  }

  // For contractAddress, re-render when chainId or contractSource changes
  if (prevProps.name === "contractAddress") {
    return prevProps.name === nextProps.name && prevProps.value === nextProps.value && prevProps.allValues?.chainId === nextProps.allValues?.chainId && prevProps.allValues?.contractSource === nextProps.allValues?.contractSource;
  }

  // For other fields, only check name, value, property, connector info, and validation
  return prevProps.name === nextProps.name && prevProps.value === nextProps.value && prevProps.property === nextProps.property && prevProps.connectorId === nextProps.connectorId && prevProps.connectorType === nextProps.connectorType && prevProps.validationError === nextProps.validationError && prevProps.workflowWalletAddress === nextProps.workflowWalletAddress && prevProps.showAnySpendFundingCta === nextProps.showAnySpendFundingCta;
});